import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'is24-corecss'
import {
  AttachmentData,
  AttachmentDataWithRequiredAttachment,
  AttachmentStatus,
  AttachmentType,
} from '../../../../../../../../../@types/types'
import { useDisableBodyScroll } from '../../../../../../../../common/hooks/useDisableBodyScroll'
import { isWebView } from '../../../../../../../../../utils/navigation'
import { useAppDispatch } from '../../../../../../../../../app/hooks'
import { deleteAttachment } from '../../../../../../../../../redux/thunks/multimedia/multimedia'
import { PicturesModalTip } from './components-mobile/picturesModalTip/PicturesModalTip'
import { DraggablePicturesGrid } from './components-mobile/draggablePicturesGrid/DraggablePicturesGrid'
import { PictureCountStatusMessage } from './components-mobile/pictureCountStatusMessage/PictureCountStatusMessage'
import { PictureCountDisplay } from './components-mobile/pictureCountDisplay/PictureCountDisplay'
import { PrimaryFooterBtn, SecondaryFooterBtn } from './components-mobile/selectionFooter/SelectionFooter'
import { useMultimediaDetailsModalContext } from '../../../multimediaDetailsModalContext/multimediaModalsContext'

export interface WebViewGalleryModalProps {
  attachments: AttachmentData[]
  isOpen: boolean
  closeModal: () => void
  isEditMode?: boolean
}

const WebViewGalleryModal: React.FC<WebViewGalleryModalProps> = ({
  attachments,
  isOpen,
  closeModal,
  isEditMode = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  useDisableBodyScroll(isOpen)
  const isMobile = isWebView()
  const { openDetailsModalForSelectedAttachments } = useMultimediaDetailsModalContext()

  // Use the attachments passed from context instead of Redux selector
  const allAttachments = attachments
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [deletedIds, setDeletedIds] = useState<string[]>([])
  const deletedIdsSet = useMemo(() => new Set(deletedIds), [deletedIds])

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([])
    }
  }, [isOpen])

  const pictureAttachments = useMemo(
    () =>
      allAttachments.filter(
        (attachment: AttachmentData) =>
          (attachment.state === AttachmentStatus.PERSISTED || attachment.state === AttachmentStatus.UPLOADING) &&
          attachment.attachmentType === AttachmentType.PICTURE &&
          (attachment.attachment || attachment.file) && // For uploading attachments, file exists instead of attachment
          !deletedIdsSet.has(attachment.attachment?.id || attachment.file?.id || '')
      ),
    [allAttachments, deletedIdsSet]
  )

  const handlePictureSelect = (attachmentId: string) => {
    // Only allow selection of persisted attachments (not uploading ones)
    const attachment = pictureAttachments.find((att: any) => (att.attachment?.id || att.file?.id) === attachmentId)
    if (attachment?.state !== AttachmentStatus.PERSISTED) {
      return // Don't allow selection of uploading attachments
    }

    setSelectedIds((prev) =>
      prev.includes(attachmentId) ? prev.filter((id) => id !== attachmentId) : [...prev, attachmentId]
    )
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return

    const selectedIdsSet = new Set(selectedIds)
    const selectedAttachments = pictureAttachments.filter((attachment: AttachmentData) =>
      selectedIdsSet.has(attachment.attachment?.id || attachment.file?.id || '')
    )

    for (const attachment of selectedAttachments) {
      if (attachment.attachment) {
        await dispatch(
          deleteAttachment(attachment.attachment.id, attachment.attachment.source, attachment.attachment.size)
        )

        // Add to deleted IDs (workaround for Redux reducer bug)
        // TODO aantkowiak: double-check if we can avoid this workaround after fixing the reducer bug
        setDeletedIds((prev) => [...prev, attachment.attachment!.id])
      }
    }

    // Clear selection after deletion
    setSelectedIds([])
  }

  const handleEditTitle = () => {
    const selectedAttachments = pictureAttachments
      .filter((attachment: AttachmentData) =>
        selectedIds.includes(attachment.attachment?.id || attachment.file?.id || '')
      )
      .filter((attachment: AttachmentData) => attachment.attachment) // Only include persisted attachments for editing
      .map((attachment: AttachmentData) => attachment as AttachmentDataWithRequiredAttachment)

    openDetailsModalForSelectedAttachments(selectedAttachments)
  }

  const selectedCount = selectedIds.length

  if (!isMobile) {
    return null
  }

  if (!isOpen) {
    return null
  }

  const primaryFooterBtn = <PrimaryFooterBtn selectedCount={selectedCount} onEditTitle={handleEditTitle} />
  const secondaryFooterBtn = (
    <SecondaryFooterBtn selectedCount={selectedCount} onDeleteSelected={handleDeleteSelected} />
  )

  return (
    <Modal
      ariaLabelCloseButton='close'
      visible={isOpen}
      title={
        isEditMode
          ? t('wizard.screen.multimedia.webview.modal.editTitle', 'Bilder bearbeiten')
          : t('wizard.screen.multimedia.webview.modal.title', 'All images')
      }
      onClose={closeModal}
      fullHeight={true}
      primaryFooterBtn={primaryFooterBtn}
      secondaryFooterBtn={secondaryFooterBtn}
    >
      {!isEditMode && <PicturesModalTip />}
      <PictureCountDisplay pictureCount={pictureAttachments.length} />
      <DraggablePicturesGrid
        attachments={pictureAttachments}
        selectedIds={selectedIds}
        onPictureSelect={handlePictureSelect}
      />
      {!isEditMode && <PictureCountStatusMessage pictureCount={pictureAttachments.length} />}
    </Modal>
  )
}

export default WebViewGalleryModal
